import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Modal, Typography } from '@crisellevaldez/speculo-ui';
import { useUser } from '../../../hooks/useUser';
import { useAuthStore } from '../../../contexts/auth/useAuthStore';
import {
  getProductionCrmConnectionStatus,
  promoteExistingProductionCrm,
  activateProductionCrmSync,
  type ProductionCrmConnectionStatus,
} from '../../../api/productionCrmConnections';
import { PRODUCTION_TEST_CRMS, type ProductionTestCrmOrgId } from '../../../api/productionTestCrmReads';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  orgId: ProductionTestCrmOrgId;
  selectedOrgId?: string;
  currentRowId?: string;
  freshDetailId?: string;
  freshDetailLoading?: boolean;
  onSuccess?: () => void;
}

/** Existing test bindings use server transitions to complete pending setup. */
export const ExistingProductionCrmBinding: React.FC<Props> = ({
  isOpen, onClose, orgId, selectedOrgId, freshDetailId,
  freshDetailLoading, onSuccess,
}) => {
  const { user, isSuperAdmin } = useUser();
  const { user: authUser, loading: authLoading } = useAuthStore();
  const authEpoch = useRef({ user: authUser, count: 0 });
  if (authEpoch.current.user !== authUser) {
    authEpoch.current = { user: authUser, count: authEpoch.current.count + 1 };
  }
  const clientAdminContext = selectedOrgId === undefined ||
    (selectedOrgId === orgId && freshDetailId === orgId && freshDetailLoading === false);
  const eligible = isOpen && import.meta.env.VITE_APP_ENV === 'production' &&
    clientAdminContext && Boolean(user?.id) && Boolean(authUser?.uid) && !authLoading;
  const identity = [isOpen, orgId, selectedOrgId, freshDetailId,
    freshDetailLoading, user?.id, isSuperAdmin, authUser?.uid,
    authLoading, authEpoch.current.count].join('|');
  const context = useRef({ identity, generation: 0 });
  if (context.current.identity !== identity) {
    context.current = { identity, generation: context.current.generation + 1 };
  }
  const generation = context.current.generation;
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  const current = (started: number) => mounted.current &&
    started === context.current.generation;
  const [state, setState] = useState<{
    generation: number;
    phase: 'loading' | 'ready' | 'error' | 'promoting';
    data?: ProductionCrmConnectionStatus;
  } | null>(null);
  const shown = state?.generation === generation ? state : null;
  const inFlight = useRef<number | null>(null);
  const expectedApp = PRODUCTION_TEST_CRMS[orgId].app;
  const valid = useCallback((data: ProductionCrmConnectionStatus) =>
    (data.status === 'connected' || data.status === 'sync_paused' ||
      data.status === 'pending_setup') &&
    data.app === expectedApp && typeof data.sync_enabled === 'boolean' &&
    typeof data.sync_eligible === 'boolean', [expectedApp]);

  useEffect(() => {
    if (!eligible || inFlight.current === generation) return;
    const started = generation;
    inFlight.current = started;
    setState({ generation: started, phase: 'loading' });
    void getProductionCrmConnectionStatus(orgId).then((data) => {
      if (current(started)) setState(valid(data)
        ? { generation: started, phase: 'ready', data }
        : { generation: started, phase: 'error' });
    }).catch(() => {
      if (current(started)) setState({ generation: started, phase: 'error' });
    }).finally(() => {
      if (inFlight.current === started) inFlight.current = null;
    });
  }, [eligible, generation, orgId, valid]);

  const promote = async () => {
    if (!eligible || !isSuperAdmin || shown?.phase !== 'ready' ||
        shown.data?.status !== 'pending_setup' || inFlight.current === generation) return;
    const started = generation;
    inFlight.current = started;
    setState({ generation: started, phase: 'promoting' });
    try {
      const result = await promoteExistingProductionCrm(orgId);
      if (result.success !== true || result.organization_id !== orgId ||
          result.app !== expectedApp || result.status !== 'active') {
        throw new Error('Connection transition unconfirmed');
      }
      if (!current(started)) return;
      const bound = await getProductionCrmConnectionStatus(orgId);
      if (!current(started)) return;
      if (!valid(bound) || !['connected', 'sync_paused'].includes(bound.status)) {
        throw new Error('Connection readback unavailable');
      }
      try {
        if (bound.sync_eligible) await activateProductionCrmSync(orgId, expectedApp, 'bidirectional');
      } catch {
        // The API-owned binding may be active even when sync is paused or its
        // activation result is uncertain. Read status; never repeat promotion.
      }
      const data = await getProductionCrmConnectionStatus(orgId);
      if (!valid(data) || !['connected', 'sync_paused'].includes(data.status)) {
        throw new Error('Connection readback unavailable');
      }
      if (current(started)) {
        setState({ generation: started, phase: 'ready', data });
        onSuccess?.();
      }
    } catch {
      if (current(started)) setState({ generation: started, phase: 'error' });
    } finally {
      if (inFlight.current === started) inFlight.current = null;
    }
  };

  return <Modal isOpen={isOpen} onClose={onClose} size="xl">
    <div className="space-y-4 p-5">
      <Typography variant="h3">{PRODUCTION_TEST_CRMS[orgId].label} connection</Typography>
      {!eligible && <p>Connection unavailable for this selection.</p>}
      {shown?.phase === 'loading' && <p>Reading connection status...</p>}
      {shown?.phase === 'error' &&
        <p>Connection status unavailable. Reopen this connection to check it before retrying.</p>}
      {shown?.phase === 'promoting' && <p>Confirming existing connection...</p>}
      {shown?.phase === 'ready' && shown.data?.status === 'pending_setup' && <>
        <p>Saved credential is awaiting connection setup.</p>
        <p>CRM record sync direction: Bidirectional CRM records</p>
        <p>Bidirectional sync can update CRM records. It does not enable calls, texts, or emails.</p>
        {!shown.data.sync_eligible && <p>CRM sync stays paused until the customer is active.</p>}
        <Button type="button" onClick={promote} disabled={!eligible || !isSuperAdmin}>
          Complete existing connection
        </Button>
      </>}
      {shown?.phase === 'ready' &&
        (shown.data?.status === 'connected' || shown.data?.status === 'sync_paused') &&
        <p>{shown.data.status === 'connected'
          ? `Connected; CRM record sync: ${shown.data.sync_direction === 'bidirectional'
            ? 'Bidirectional' : shown.data.sync_direction === 'inbound'
              ? 'Inbound only' : 'Direction unavailable'}`
          : 'Connected; sync paused'}</p>}
    </div>
  </Modal>;
};
